import React from "react";
import {
  Grid,
  TextField,
  Autocomplete,
  FormControlLabel,
  Checkbox,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
} from "@mui/material";
import { MIN_CONTRACT_DATE } from "utils/date-limits";
import {
  formatRateAbusivenessMessage,
  isRateBelowAbusiveThreshold,
} from "utils/rate-abusiveness";
import { IconUser } from "@tabler/icons-react";
import TabPanel from "components/ui/tabs/TabPanel";
import {
  ChildContractPane,
  ContractProcessTabs,
} from "components/contracts/ContractLoansPanel";
import ContractValuesFields from "components/contracts/ContractValuesFields";
import { buildContractTabs, loanActionPermissions } from "utils/contract-loans";
import {
  asyncAutocompleteListboxProps,
  buildBankSearchUrl,
  buildCustomerSearchUrl,
  handleAsyncAutocompleteInputChange,
  noLocalFilter,
  resolveSelectedEntity,
  sameEntityId,
  useAsyncEntitySearch,
} from "utils/entity-autocomplete";

const STATUSES_REQUIRING_MOTIVO_LAWYER = new Set(["CANCELADO", "FALTAM_DOCUMENTOS"]);
const STATUSES_WITH_RESPONSIBLE_USER = STATUSES_REQUIRING_MOTIVO_LAWYER;

const ContractForm = ({
  currentContract,
  setCurrentContract,
  customers,
  banks,
  lawyers,
  salesConsultants,
  contractTypes,
  contractCategories,
  contractStatuses,
  users = [],
  tabIndex,
  setTabIndex,
  childSection = "values",
  onChildSection,
  calculationBody = null,
  formatDocument,
  calculatePgto,
  calculateInstallmentQuestionedValue,
  calculateYearlyInterest,
  calculateDaysDifference,
  setInterestRateAverageMonthlyValue,
  onCustomerChange,
  customerBankIds = [],
  fieldsWithErrors = [],
  permissions = {
    can_create_contracts: true,
    can_edit_contracts: true,
    can_delete_contracts: true,
    can_file_contracts: true,
  },
  isEditing = false,
  bulkMode = false,
  bulkEditableFields = [],
  lockCustomer = false,
  loanEditor = null,
}) => {
  const {
    options: customerOptions,
    loading: customersLoading,
    search: searchCustomers,
    listboxKey: customerListboxKey,
  } = useAsyncEntitySearch({
    buildUrl: buildCustomerSearchUrl,
    selectedEntity: currentContract.customer,
  });

  const {
    options: bankOptions,
    loading: banksLoading,
    search: searchBanks,
    listboxKey: bankListboxKey,
  } = useAsyncEntitySearch({
    buildUrl: buildBankSearchUrl,
    selectedEntity: currentContract.bank,
  });

  // Se o usuário só tem permissão de ajuizar (e não de editar),
  // apenas campos operacionais de status ficam habilitados
  const isFileOnlyMode = isEditing && !permissions.can_edit_contracts && permissions.can_file_contracts;
  const FILE_ONLY_EDITABLE_FIELDS = new Set([
    "status",
    "service_obs",
    "process_protocol_obs",
    "complaint_protocol_number",
    "lawyer",
    "responsible_user",
  ]);
  const bulkEditableSet = React.useMemo(() => new Set(bulkEditableFields || []), [bulkEditableFields]);
  const isFieldDisabled = (fieldKey) => {
    if (isFileOnlyMode) return !FILE_ONLY_EDITABLE_FIELDS.has(fieldKey);
    if (bulkMode) return !bulkEditableSet.has(fieldKey);
    return false;
  };
  const loanPermissions = loanActionPermissions(permissions, { isFileOnlyMode, bulkMode });
  const childDraft = loanEditor?.childContract && !loanEditor.childContract.id ? loanEditor.childContract : null;
  const contractTabs = loanEditor ? buildContractTabs(currentContract, childDraft) : [];
  const activeLoanTab = typeof tabIndex === "string" && tabIndex.startsWith("loan:");
  const handleProcessTabChange = (event, newValue) => {
    if (newValue === "add") {
      loanEditor?.onStartNewChild();
      return;
    }
    if (newValue === "loan:new") {
      loanEditor?.onResumeDraft();
      return;
    }
    if (typeof newValue === "string" && newValue.startsWith("loan:")) {
      loanEditor?.onSelectChild(Number(newValue.slice(5)));
      return;
    }
    if (loanEditor?.onShowPrincipal) {
      loanEditor.onShowPrincipal(newValue);
      return;
    }
    setTabIndex(newValue);
  };

  const displayValue = (fieldKey, value) => {
    // Em bulkMode, campos não editáveis devem ficar vazios
    if (bulkMode && !bulkEditableSet.has(fieldKey)) return "";
    return value ?? "";
  };

  const isRateNonAbusive = isRateBelowAbusiveThreshold(
    currentContract.interest_rate_monthly_value,
    currentContract.interest_rate_average_monthly_value,
    currentContract.category
  );
  const rateAbusivenessMessage = isRateNonAbusive
    ? formatRateAbusivenessMessage(
        currentContract.interest_rate_monthly_value,
        currentContract.interest_rate_average_monthly_value
      )
    : "";
  // Mapeamento: cada campo do formulário pode ter vários nomes de erro (backend custom, DRF, etc.)
  // { label, exact: true } = match exato apenas (evita "Parcelas" casar com "Parcelas pagas")
  const ERROR_LABELS = {
    number: ["Nº contrato", "Número do contrato"],
    customer: ["Contratante", "Cliente"],
    bank: ["Banco"],
    date: ["Data do fechamento do contrato", "Data do contrato"],
    status: ["Situação", "Status"],
    state: ["UF", "Estado"],
    sales_consultant: ["Consultor de vendas"],
    contracted_value: ["Valor contratado"],
    installments: [{ label: "Parcelas", exact: true }, "Número de parcelas"],
    installment_value: ["Valor das parcelas", "Valor da parcela"],
    interest_rate_monthly_value: ["Taxa de juros contratada A.M.", "Taxa de juros mensal", "Taxa de juros contratada"],
    contract_at: ["Data da contratação", "Data do contrato"],
    first_installment_at: ["Data primeira parcela", "Data da primeira parcela"],
    installments_payed: ["Parcelas pagas"],
    contract_type: ["Tipo de contrato"],
    updated_at: ["Atualizado até"],
    filed_at: ["Ajuizado em", "Data de protocolo"],
    file_at: ["Ajuizar em", "Data de arquivamento"],
    interest_rate_average_monthly_value: ["Taxa de juros média A.M.", "Taxa de juros média mensal"],
    lawyer: ["Advogado", "Advogado da inicial"],
    process_protocol_obs: ["Observação do protocolo do processo"],
  };
  const requiresStatusMotivoAndLawyer = () => {
    const status = currentContract.status;
    if (!STATUSES_REQUIRING_MOTIVO_LAWYER.has(status)) return false;
    if (bulkMode) return true;
    const originalStatus = currentContract._originalStatus;
    if (!originalStatus) return true;
    return originalStatus !== status;
  };
  const showStatusMotivoField = requiresStatusMotivoAndLawyer();
  const lawyerRequired = showStatusMotivoField;
  const processProtocolObsRequired = showStatusMotivoField;
  const hasError = (fieldKey) => {
    const labels = ERROR_LABELS[fieldKey];
    if (!labels) return false;
    return fieldsWithErrors.some((f) =>
      labels.some((l) => {
        const label = typeof l === "object" ? l.label : l;
        const exact = typeof l === "object" && l.exact;
        if (exact) return f === label;
        return f.startsWith(label) || f === label;
      })
    );
  };
  return (
    <>
      {/* Aviso quando em modo somente ajuizar */}
      {isFileOnlyMode && (
        <Box sx={{ 
          backgroundColor: '#fff3e0', 
          border: '1px solid #ffb74d', 
          borderRadius: 1, 
          p: 2, 
          mb: 2,
          mt: 2 
        }}>
          <Typography variant="body2" color="warning.dark">
            <strong>Modo Ajuizar:</strong> Você pode alterar situação, observações e advogado da inicial.
          </Typography>
        </Box>
      )}

      {isRateNonAbusive && !bulkMode && (
        <Alert severity="warning" sx={{ mt: isFileOnlyMode ? 0 : 2, mb: 2 }}>
          {rateAbusivenessMessage}
        </Alert>
      )}

      {/* Campos do topo - sempre visíveis */}
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            label="Nº contrato *"
            value={displayValue("number", currentContract.number)}
            onChange={(e) =>
              setCurrentContract({ ...currentContract, number: e.target.value })
            }
            fullWidth
            inputProps={{ maxLength: 30 }}
            autoComplete="new-password"
            sx={{ mt: 2 }}
            disabled={isFieldDisabled("number")}
            error={hasError("number")}
            helperText={hasError("number") ? "Campo obrigatório não preenchido" : undefined}
          />
        </Grid>

        <Grid item xs={8}>
          <Autocomplete
            options={customerOptions}
            getOptionLabel={(option) => option?.name || ""}
            isOptionEqualToValue={(option, value) => sameEntityId(option?.id, value?.id)}
            value={
              bulkMode && isFieldDisabled("customer")
                ? null
                : resolveSelectedEntity(currentContract.customer, customerOptions, customers)
            }
            onChange={async (event, newValue) => {
              if (lockCustomer) return;
              let customer = newValue;
              if (newValue?.id && !newValue.cpf) {
                try {
                  const res = await fetch(`/backend/customers/${newValue.id}/`, {
                    credentials: "include",
                  });
                  if (res.ok) {
                    const fullCustomer = await res.json();
                    customer = { ...newValue, cpf: fullCustomer.cpf };
                  }
                } catch (e) { /* ignora */ }
              }
              let update = { ...currentContract, customer: customer || null };
              setCurrentContract(update);
              if (onCustomerChange) onCustomerChange(customer?.id || null);
              const bankId = currentContract.bank?.id ?? currentContract.bank_id;
              if (customer?.id && bankId && (currentContract.status === 'AJUIZAR' || currentContract.status === 'REAJUIZAR') && !currentContract.file_at) {
                try {
                  const res = await fetch(`/backend/contracts/next-file-at/?customer_id=${customer.id}&bank_id=${bankId}`, { credentials: 'include' });
                  if (res.ok) {
                    const data = await res.json();
                    if (data.file_at) setCurrentContract({ ...update, file_at: data.file_at });
                  }
                } catch (e) { /* ignora */ }
              }
            }}
            onInputChange={(event, newInputValue, reason) =>
              handleAsyncAutocompleteInputChange(searchCustomers, reason, newInputValue)
            }
            onOpen={() => searchCustomers("")}
            loading={customersLoading}
            disabled={isFieldDisabled("customer") || lockCustomer}
            filterOptions={noLocalFilter}
            ListboxProps={asyncAutocompleteListboxProps(customerListboxKey)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Contratante *"
                fullWidth
                autoComplete="new-password"
                error={hasError("customer")}
                helperText={
                  hasError("customer")
                    ? "Campo obrigatório não preenchido"
                    : lockCustomer
                      ? "Cliente definido pela visão do cliente."
                      : undefined
                }
              />
            )}
          />
        </Grid>

        <Grid item xs={4}>
          <TextField
            label="CPF"
            value={bulkMode ? "" : formatDocument(currentContract.customer?.cpf || "")}
            fullWidth
            disabled
            autoComplete="new-password"
          />
        </Grid>
      </Grid>

      {/* Dados do banco - embaixo dos 3 campos do topo */}
      <Grid container spacing={2}>
        <Grid item xs={8}>
          <Autocomplete
            options={bankOptions}
            getOptionLabel={(option) => option?.name || ""}
            isOptionEqualToValue={(option, value) => sameEntityId(option?.id, value?.id)}
            value={
              bulkMode && isFieldDisabled("bank")
                ? null
                : resolveSelectedEntity(currentContract.bank, bankOptions, banks)
            }
            onChange={async (event, newValue) => {
              let update = { ...currentContract, bank: newValue || null };
              setCurrentContract(update);
              const customerId = currentContract.customer?.id ?? currentContract.customer_id;
              const bankId = newValue?.id;
              if (customerId && bankId && (currentContract.status === 'AJUIZAR' || currentContract.status === 'REAJUIZAR') && !currentContract.file_at) {
                try {
                  const res = await fetch(`/backend/contracts/next-file-at/?customer_id=${customerId}&bank_id=${bankId}`, { credentials: 'include' });
                  if (res.ok) {
                    const data = await res.json();
                    if (data.file_at) setCurrentContract({ ...update, file_at: data.file_at });
                  }
                } catch (e) { /* ignora */ }
              }
            }}
            onInputChange={(event, newInputValue, reason) =>
              handleAsyncAutocompleteInputChange(searchBanks, reason, newInputValue)
            }
            onOpen={() => searchBanks("")}
            loading={banksLoading}
            disabled={isFieldDisabled("bank")}
            filterOptions={noLocalFilter}
            ListboxProps={asyncAutocompleteListboxProps(bankListboxKey)}
            renderOption={(props, option) => {
              const isCustomerBank = customerBankIds.includes(option.id);
              return (
                <Box component="li" {...props} key={option.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    {isCustomerBank && (
                      <IconUser 
                        size={18} 
                        style={{ 
                          color: '#1976d2',
                          flexShrink: 0 
                        }} 
                      />
                    )}
                    <Box component="span" sx={{ flex: 1 }}>
                      {option.name}
                    </Box>
                  </Box>
                </Box>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Banco *"
                fullWidth
                sx={{ mt: 2 }}
                error={hasError("bank")}
                helperText={hasError("bank") ? "Campo obrigatório não preenchido" : undefined}
                autoComplete="new-password"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: currentContract.bank?.id && customerBankIds.includes(currentContract.bank.id) ? (
                    <>
                      <IconUser 
                        size={18} 
                        style={{ 
                          color: '#1976d2',
                          marginRight: 8 
                        }} 
                      />
                      {params.InputProps.startAdornment}
                    </>
                  ) : params.InputProps.startAdornment,
                }}
              />
            )}
          />
        </Grid>

        <Grid item xs={4}>
          <TextField
            label="CNPJ"
            value={bulkMode ? "" : formatDocument(currentContract.bank?.cnpj || "")}
            fullWidth
            sx={{ mt: 2 }}
            disabled
            autoComplete="new-password"
          />
        </Grid>
      </Grid>

      {/* Abas - logo após os campos Banco e CNPJ */}
      <Box sx={{ mt: 3 }}>
        <ContractProcessTabs
          value={tabIndex}
          onChange={handleProcessTabChange}
          bulkMode={bulkMode}
          showAdd={Boolean(loanEditor) && loanPermissions.canCreate}
          addDisabled={!currentContract?.id}
          extraTabs={contractTabs}
        />

        {/* Conteúdo das abas - demais campos */}
        <TabPanel value={tabIndex} index={0}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField
              label="Data do fechamento do contrato *"
              type="date"
              value={displayValue("date", currentContract.date)}
              onChange={(e) => {
                setCurrentContract({
                  ...currentContract,
                  date: e.target.value,
                });
              }}
              fullWidth
              autoComplete="new-password"
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: MIN_CONTRACT_DATE }}
              disabled={isFieldDisabled("date")}
              error={hasError("date")}
              helperText={hasError("date") ? "Campo obrigatório não preenchido" : undefined}
            />
          </Grid>

          <Grid item xs={6}>
            <Autocomplete
              options={contractCategories}
              getOptionLabel={(option) => option.label}
              value={
                contractCategories.find((c) => c.value === currentContract.category) || null
              }
              onChange={(event, newValue) => {
                setCurrentContract({
                  ...currentContract,
                  category: newValue ? newValue.value : null,
                });
              }}
              disabled={isFieldDisabled("category")}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Categoria"
                  fullWidth
                  autoComplete="new-password"
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Nº processo"
              value={displayValue("process_number", currentContract.process_number)}
              onChange={(e) =>
                setCurrentContract({ ...currentContract, process_number: e.target.value })
              }
              fullWidth
              inputProps={{ maxLength: 30 }}
              autoComplete="new-password"
              disabled={isFieldDisabled("process_number")}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Nº protocolo de reclamação"
              value={displayValue("complaint_protocol_number", currentContract.complaint_protocol_number)}
              onChange={(e) =>
                setCurrentContract({ ...currentContract, complaint_protocol_number: e.target.value })
              }
              fullWidth
              inputProps={{ maxLength: 30 }}
              autoComplete="new-password"
              disabled={isFieldDisabled("complaint_protocol_number")}
            />
          </Grid>

          <Grid item xs={STATUSES_WITH_RESPONSIBLE_USER.has(currentContract.status) ? 3 : 6}>
            <Autocomplete
              options={contractStatuses}
              getOptionLabel={(option) => option.label}
              value={
                contractStatuses.find((c) => c.value === currentContract.status) || null
              }
              onChange={async (event, newValue) => {
                const nextStatus = newValue ? newValue.value : null;
                let update = {
                  ...currentContract,
                  status: nextStatus,
                  responsible_user: STATUSES_WITH_RESPONSIBLE_USER.has(nextStatus) ? currentContract.responsible_user : null,
                };
                if ((newValue?.value === 'AJUIZAR' || newValue?.value === 'REAJUIZAR') && !currentContract.file_at) {
                  const customerId = currentContract.customer?.id ?? currentContract.customer_id ?? (typeof currentContract.customer === 'number' ? currentContract.customer : null);
                  const bankId = currentContract.bank?.id ?? currentContract.bank_id ?? (typeof currentContract.bank === 'number' ? currentContract.bank : null);
                  if (customerId && bankId) {
                    try {
                      const res = await fetch(`/backend/contracts/next-file-at/?customer_id=${customerId}&bank_id=${bankId}`, { credentials: 'include' });
                      if (res.ok) {
                        const data = await res.json();
                        if (data.file_at) update = { ...update, file_at: data.file_at };
                      }
                    } catch (e) { /* ignora erro de rede */ }
                  }
                }
                setCurrentContract(update);
              }}
              disabled={isFieldDisabled("status")}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Situação *"
                  fullWidth
                  autoComplete="new-password"
                  error={hasError("status")}
                  helperText={hasError("status") ? "Campo obrigatório não preenchido" : undefined}
                />
              )}
            />
          </Grid>

          {/* Campo de usuário responsável - aparece para CANCELADO e FALTAM DOCUMENTOS */}
          {STATUSES_WITH_RESPONSIBLE_USER.has(currentContract.status) && (
            <Grid item xs={6}>
              <Autocomplete
                options={users}
                getOptionLabel={(option) => {
                  const fullName = `${option.first_name || ''} ${option.last_name || ''}`.trim();
                  return fullName || option.username;
                }}
                value={
                  users.find((u) => u.id === currentContract.responsible_user?.id) || null
                }
                onChange={(event, newValue) => {
                  setCurrentContract({
                    ...currentContract,
                    responsible_user: newValue || null,
                  });
                }}
                disabled={isFieldDisabled("responsible_user")}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Usuário responsável"
                    fullWidth
                    autoComplete="new-password"
                    helperText="Selecione o usuário responsável pelo contrato"
                  />
                )}
              />
            </Grid>
          )}

          <Grid item xs={3}>
            <TextField
              label="Ajuizado em"
              type="date"
              value={displayValue("filed_at", currentContract.filed_at)}
              onChange={(e) => {
                setCurrentContract({
                  ...currentContract,
                  filed_at: e.target.value || null,
                });
              }}
              fullWidth
              autoComplete="new-password"
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: MIN_CONTRACT_DATE }}
              disabled={isFieldDisabled("filed_at")}
              error={hasError("filed_at")}
              helperText={hasError("filed_at") ? "Deve ser a partir de 01/01/2000" : undefined}
            />
          </Grid>

          <Grid item xs={3}>
            <Autocomplete
              options={lawyers}
              getOptionLabel={(option) => option.name}
              value={
                lawyers.find(
                  (l) => l.id === currentContract.lawyer?.id
                ) || null
              }
              onChange={(event, newValue) => {
                setCurrentContract({
                  ...currentContract,
                  lawyer: newValue || null,
                });
              }}
              disabled={isFieldDisabled("lawyer")}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={lawyerRequired ? "Advogado da inicial *" : "Advogado da inicial"}
                  fullWidth
                  autoComplete="new-password"
                  error={hasError("lawyer")}
                  helperText={
                    hasError("lawyer")
                      ? "Advogado é obrigatório para este status"
                      : undefined
                  }
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Observação do atendimento"
              value={displayValue("service_obs", currentContract.service_obs)}
              onChange={(e) =>
                setCurrentContract({ ...currentContract, service_obs: e.target.value })
              }
              fullWidth
              inputProps={{ maxLength: 100 }}
              autoComplete="new-password"
              disabled={isFieldDisabled("service_obs")}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label={
                processProtocolObsRequired
                  ? "Observação do protocolo do processo *"
                  : "Observação do protocolo do processo"
              }
              value={displayValue("process_protocol_obs", currentContract.process_protocol_obs)}
              onChange={(e) =>
                setCurrentContract({ ...currentContract, process_protocol_obs: e.target.value })
              }
              fullWidth
              multiline
              rows={processProtocolObsRequired ? 2 : 1}
              inputProps={{ maxLength: 100 }}
              autoComplete="new-password"
              disabled={isFieldDisabled("process_protocol_obs")}
              error={hasError("process_protocol_obs")}
              helperText={
                hasError("process_protocol_obs")
                  ? "Informe a justificativa ao alterar para este status"
                  : processProtocolObsRequired
                    ? "Obrigatória ao cancelar ou marcar falta de documentos"
                    : undefined
              }
            />
          </Grid>

          <Grid item xs={2}>
            <TextField
              label="UF *"
              value={displayValue("state", currentContract.state)}
              onChange={(e) =>
                setCurrentContract({
                  ...currentContract,
                  state: e.target.value,
                })
              }
              fullWidth
              inputProps={{ maxLength: 2 }}
              autoComplete="new-password"
              disabled={isFieldDisabled("state")}
              error={hasError("state")}
              helperText={hasError("state") ? "Campo obrigatório não preenchido" : undefined}
            />
          </Grid>

          <Grid item xs={10}>
            <Autocomplete
              options={salesConsultants}
              getOptionLabel={(option) => option.name}
              value={
                salesConsultants.find(
                  (l) => l.id === currentContract.sales_consultant?.id
                ) || null
              }
              onChange={(event, newValue) => {
                setCurrentContract({
                  ...currentContract,
                  sales_consultant: newValue || null,
                });
              }}
              disabled={isFieldDisabled("sales_consultant")}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Consultor de vendas *"
                  fullWidth
                  autoComplete="new-password"
                  error={hasError("sales_consultant")}
                  helperText={hasError("sales_consultant") ? "Campo obrigatório não preenchido" : undefined}
                />
              )}
            />
          </Grid>

          <Grid item xs={8}>
            <Autocomplete
              options={lawyers}
              getOptionLabel={(option) => option.name}
              value={
                bulkMode && isFieldDisabled("lawyer_wallet")
                  ? null
                  : (lawyers.find((l) => l.id === currentContract.lawyer_wallet?.id) || null)
              }
              onChange={(event, newValue) => {
                setCurrentContract({
                  ...currentContract,
                  lawyer_wallet: newValue || null,
                });
              }}
              disabled={isFieldDisabled("lawyer_wallet")}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Carteira"
                  fullWidth
                  autoComplete="new-password"
                />
              )}
            />
          </Grid>

          <Grid item xs={4}>
            <TextField
              label="Ajuizar em"
              type="date"
              value={displayValue("file_at", currentContract.file_at)}
              onChange={(e) => {
                setCurrentContract({
                  ...currentContract,
                  file_at: e.target.value || null,
                });
              }}
              fullWidth
              autoComplete="new-password"
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: MIN_CONTRACT_DATE }}
              disabled={isFieldDisabled("file_at")}
              error={hasError("file_at")}
              helperText={hasError("file_at") ? "Deve ser a partir de 01/01/2000" : undefined}
            />
          </Grid>

          <Grid item xs={12} sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
            {bulkMode ? (
              <>
                <FormControl size="small" sx={{ minWidth: 220 }}>
                  <InputLabel id="bulk-has-ajg-contractform-label">Tem AJG</InputLabel>
                  <Select
                    labelId="bulk-has-ajg-contractform-label"
                    label="Tem AJG"
                    value={currentContract.has_ajg ?? ""}
                    onChange={(e) =>
                      setCurrentContract({
                        ...currentContract,
                        has_ajg: e.target.value === "" ? null : e.target.value === "true",
                      })
                    }
                    disabled={isFieldDisabled("has_ajg")}
                  >
                    <MenuItem value="">Não alterar</MenuItem>
                    <MenuItem value="true">Sim</MenuItem>
                    <MenuItem value="false">Não</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 260 }}>
                  <InputLabel id="bulk-has-video-contractform-label">Tem vídeo pós vendas</InputLabel>
                  <Select
                    labelId="bulk-has-video-contractform-label"
                    label="Tem vídeo pós vendas"
                    value={currentContract.has_after_sales_video ?? ""}
                    onChange={(e) =>
                      setCurrentContract({
                        ...currentContract,
                        has_after_sales_video: e.target.value === "" ? null : e.target.value === "true",
                      })
                    }
                    disabled={isFieldDisabled("has_after_sales_video")}
                  >
                    <MenuItem value="">Não alterar</MenuItem>
                    <MenuItem value="true">Sim</MenuItem>
                    <MenuItem value="false">Não</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 240 }}>
                  <InputLabel id="bulk-declares-irpf-contractform-label">IRPF</InputLabel>
                  <Select
                    labelId="bulk-declares-irpf-contractform-label"
                    label="IRPF"
                    value={currentContract.declares_irpf ?? ""}
                    onChange={(e) =>
                      setCurrentContract({
                        ...currentContract,
                        declares_irpf: e.target.value === "" ? null : e.target.value === "true",
                      })
                    }
                    disabled={isFieldDisabled("declares_irpf")}
                  >
                    <MenuItem value="">Não alterar</MenuItem>
                    <MenuItem value="true">Declara IRPF</MenuItem>
                    <MenuItem value="false">Isento de IRPF</MenuItem>
                  </Select>
                </FormControl>
              </>
            ) : (
              <>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!currentContract.has_ajg}
                      onChange={(e) =>
                        setCurrentContract({
                          ...currentContract,
                          has_ajg: e.target.checked,
                        })
                      }
                      disabled={isFieldDisabled("has_ajg")}
                    />
                  }
                  label="Tem AJG"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!currentContract.has_after_sales_video}
                      onChange={(e) =>
                        setCurrentContract({
                          ...currentContract,
                          has_after_sales_video: e.target.checked,
                        })
                      }
                      disabled={isFieldDisabled("has_after_sales_video")}
                    />
                  }
                  label="Tem vídeo pós vendas"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!currentContract.declares_irpf}
                      onChange={(e) =>
                        setCurrentContract({
                          ...currentContract,
                          declares_irpf: e.target.checked,
                        })
                      }
                      disabled={isFieldDisabled("declares_irpf")}
                    />
                  }
                  label="Declara IRPF"
                />
              </>
            )}
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={tabIndex} index={1}>
          <ContractValuesFields
            contract={currentContract}
            setContract={setCurrentContract}
            contractTypes={contractTypes}
            isFieldDisabled={isFieldDisabled}
            hasError={hasError}
            calculatePgto={calculatePgto}
            calculateInstallmentQuestionedValue={calculateInstallmentQuestionedValue}
            calculateYearlyInterest={calculateYearlyInterest}
            calculateDaysDifference={calculateDaysDifference}
            onLookupAverageRate={setInterestRateAverageMonthlyValue}
          />
        </TabPanel>
        {activeLoanTab && loanEditor && (
          <Box sx={{ px: 3, pt: 1.5, pb: 2 }}>
            <ChildContractPane
              loanEditor={loanEditor}
              contractCategories={contractCategories}
              contractTypes={contractTypes}
              isFieldDisabled={isFieldDisabled}
              hasError={hasError}
              calculatePgto={calculatePgto}
              calculateInstallmentQuestionedValue={calculateInstallmentQuestionedValue}
              calculateYearlyInterest={calculateYearlyInterest}
              calculateDaysDifference={calculateDaysDifference}
              isFileOnlyMode={isFileOnlyMode}
              bulkMode={bulkMode}
              childSection={childSection}
              onChildSection={onChildSection}
              calculationBody={calculationBody}
            />
          </Box>
        )}
      </Box>
    </>
  );
};

export default ContractForm; 